import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { StatCard, Card, PageHeader, statusBadge, formatCurrency, formatDate, Badge } from '../ui';
import { Users, DollarSign, Trophy, Calendar, AlertTriangle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const revenueData = [
  { month: 'Jan', revenue: 1200 },
  { month: 'Feb', revenue: 2100 },
  { month: 'Mar', revenue: 3800 },
  { month: 'Apr', revenue: 6200 },
  { month: 'May', revenue: 9400 },
  { month: 'Jun', revenue: 14200 },
];

export default function Dashboard() {
  const { teams, players, registrations, payments, tournaments, games, announcements } = useApp();
  const { user, isAdmin, isCoach, isParent } = useAuth();
  const navigate = useNavigate();

  const totalRevenue = payments.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0);
  const pendingRevenue = payments.filter(p => ['pending','overdue','partial'].includes(p.status)).reduce((s, p) => s + p.amount, 0);
  const upcomingTournaments = tournaments.filter(t => t.status === 'open' || t.status === 'upcoming');

  const recentRegs = [...registrations].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6);
  const upcomingGames = [...games].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 5);

  const getTeam = id => teams.find(t => t.id === id);
  const getTournament = id => tournaments.find(t => t.id === id);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const highPriority = announcements.filter(a => a.priority === 'high');

  return (
    <div>
      <PageHeader
        title={`${greeting()}, ${user?.name?.split(' ')[0]}`}
        subtitle={isAdmin ? 'Here\'s what\'s happening across UC Elites' : isCoach ? 'Your team overview' : 'Your player updates'}
      />

      {/* Alerts */}
      {isAdmin && highPriority.length > 0 && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {highPriority.map(a => (
            <div key={a.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderLeft: '3px solid var(--gold-main)', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <AlertTriangle size={16} color="var(--gold-main)" />
              <div>
                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--gold-main)' }}>{a.title}</span>
                <span style={{ fontSize: 13, color: '#aaaaaa', marginLeft: 6 }}>{a.body}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: '1.5rem' }}>
        <StatCard label="Registered teams" value={teams.length} sub={`${registrations.filter(r=>r.status==='confirmed').length} confirmed`} icon={<Users size={18} />} />
        <StatCard label="Active players" value={players.length} sub={`Across ${teams.length} teams`} icon={<Users size={18} />} />
        {isAdmin && <StatCard label="Revenue collected" value={formatCurrency(totalRevenue)} sub={`${formatCurrency(pendingRevenue)} pending`} subColor="var(--gold-main)" icon={<DollarSign size={18} />} />}
        <StatCard label="Active events" value={upcomingTournaments.length} sub="Open for registration" icon={<Trophy size={18} />} />
        <StatCard label="Upcoming games" value={upcomingGames.length} sub="Next 7 days" icon={<Calendar size={18} />} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.25rem' }}>
        {/* Main column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Revenue chart (admin only) */}
          {isAdmin && (
            <Card>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: '1.25rem', color: 'var(--text-primary)' }}>Revenue trend</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={revenueData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--gold-main)" stopOpacity={0.05}/>
                      <stop offset="95%" stopColor="var(--gold-main)" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [formatCurrency(v), 'Revenue']} contentStyle={{ fontSize: 13, borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: '#111111', color: '#ffffff' }} />
                  <Area type="monotone" dataKey="revenue" stroke="var(--gold-main)" strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Recent registrations */}
          <Card padding="0">
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Recent registrations</h3>
              <button onClick={() => navigate('/registrations')} style={{ fontSize: 13, color: 'var(--gold-main)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>View all →</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: 'var(--surface-1)' }}>
                    {['Team','Age group','Event','Status','Payment'].map(h => (
                      <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {recentRegs.map(reg => {
                    const team = getTeam(reg.teamId);
                    const tourn = getTournament(reg.tournamentId);
                    const pay = payments.find(p => p.id === reg.paymentId);
                    return (
                      <tr key={reg.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-1)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: team?.color || '#94a3b8' }} />
                            <span style={{ fontWeight: 600 }}>{team?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>{reg.ageGroup}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>{tourn?.name}</td>
                        <td style={{ padding: '10px 14px' }}>{statusBadge(reg.status)}</td>
                        <td style={{ padding: '10px 14px' }}>{pay ? statusBadge(pay.status) : <Badge variant="gray">No invoice</Badge>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Upcoming events */}
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: '1rem', color: 'var(--text-primary)' }}>Open events</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {upcomingTournaments.map(t => (
                <div key={t.id} onClick={() => navigate('/tournaments')} style={{ padding: '10px 12px', background: 'var(--surface-1)', borderRadius: 10, cursor: 'pointer', border: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--gold-main)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.dates[0]} · {t.location}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {t.ageGroups.reduce((s, ag) => s + ag.teams.length, 0)} / {t.ageGroups.reduce((s, ag) => s + ag.maxTeams, 0)} teams
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Today's schedule preview */}
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: '1rem', color: 'var(--text-primary)' }}>Upcoming games</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {upcomingGames.map(g => {
                const home = getTeam(g.homeTeamId);
                const away = getTeam(g.awayTeamId);
                return (
                  <div key={g.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold-main)', minWidth: 44, marginTop: 1 }}>{g.time}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{home?.name} vs {away?.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{g.ageGroup} · {g.field} · {g.round}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Compliance alerts (admin) */}
          {isAdmin && (
            <Card>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: '1rem', color: 'var(--text-primary)' }}>Compliance</h3>
              {[
                { label: 'Missing waivers', count: players.filter(p => !p.waiver).length, color: '#e53935' },
                { label: 'Missing medical', count: players.filter(p => !p.medical).length, color: '#f57c00' },
                { label: 'Incomplete registrations', count: registrations.filter(r => r.status === 'incomplete').length, color: '#f57c00' },
                { label: 'Overdue payments', count: payments.filter(p => p.status === 'overdue').length, color: '#e53935' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{item.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: item.count > 0 ? item.color : '#43a047' }}>{item.count}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
